import React, { Fragment, useState } from "react";
import { useController } from "react-hook-form";
import { CCollapse } from "@coreui/react";

export const Permissions = ({
  allPermissions,
  control,
  register,
  canUpdateLocations,
  canChangeEmails,
  canUpdateCorporateReports,
  canSendInactiveUsersEmail,
  canSendLocationComparisonDailyEmail,
  setValue,
  watch
}) => {
  const customPermissions = [
    { id: "send_separate_daily_reports", name: "Send Separate Daily Email for Each Store" }
  ];

  if (canSendInactiveUsersEmail) {
    customPermissions.push({ id: "send_inactive_users_email", name: "Send Inactive Users Email" });
  }

  const watchedValues = watch(customPermissions.map((customPermission) => customPermission.id));

  const { field: locations } = useController({ control, name: "clubready_store_ids" });
  const { field: permissions } = useController({ control, name: "permission_ids" });
  const { field: corporatePermissions } = useController({ control, name: "corporate_report_ids" });

  const [permissionTypeKey, setPermissionTypeKey] = useState("");

  const [currentGroupName, setCurrentGroupName] = useState("");

  const onPermissionTypeClick = (type) => {
    setPermissionTypeKey(type);

    if (type === permissionTypeKey) {
      setPermissionTypeKey("");
      setCurrentGroupName("");
    }
  };

  const onGroupPermissionClick = (e) => {
    let targetText = e.target.innerText;

    if (targetText === currentGroupName) {
      setCurrentGroupName("");
      return;
    }

    setCurrentGroupName(targetText);
  };

  const onPermissionClick = (e, state) => {
    e.stopPropagation();
    let checked = e.target.checked;
    let val = +e.target.value;

    if (!checked) {
      delete state.value[val];
      state.onChange(state.value);
      return;
    }

    state.onChange({ ...state.value, [val]: checked });
  };

  const handleCustomControl = (checked) => {
    customPermissions.forEach((customPermission) =>
      setValue(customPermission.id, checked, { shouldDirty: true })
    );
  };

  const onAllClick = ({ e, groupParent, options, state }) => {
    let currentPermissions = options.map((p) => p.id);
    let checked = e.target.checked;

    if (groupParent === "notifications_emails_goals") {
      handleCustomControl(checked);
    }

    if (checked) {
      let ids = currentPermissions.reduce((acc, val) => ({ ...acc, [val]: true }), {});
      state.onChange({ ...state.value, ...ids });
    } else {
      for (let key in state.value) {
        if (currentPermissions.includes(+key)) {
          delete state.value[key];
        }
      }

      state.onChange(state.value);
    }
  };

  const isAllChecked = (options, state) => {
    let allPermissionIdsPerGroup = options.map((permission) => permission.id);
    return allPermissionIdsPerGroup.every((id) => state.value[id]);
  };

  const areAllEmailsChecked = (options, state) => {
    let isChecked = isAllChecked(options, state);

    let customPermissionChecked = customPermissions.every((_, index) => watchedValues[index]);

    return isChecked && customPermissionChecked;
  };

  const renderInnerControls = ({ groupParent, group, options, state }) => (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ paddingLeft: "40px", paddingBottom: "15px", width: "100%" }}
    >
      <div className="form-check" key={group}>
        <>
          <input
            className="form-check-input"
            type="checkbox"
            value={group}
            id={group}
            onChange={(e) => onAllClick({ e, groupParent, options, state })}
            checked={
              groupParent == "notifications_emails_goals"
                ? areAllEmailsChecked(options, state)
                : isAllChecked(options, state)
            }
          />
          <label className="form-check-label" htmlFor={group}>
            Check All / Uncheck All
          </label>
        </>
      </div>

      {options.map((permission) => {
        if (
          permission.name === "Location Comparison Daily Email" &&
          !canSendLocationComparisonDailyEmail
        )
          return null;
        return (
          <div className="form-check" key={permission.id}>
            <>
              <input
                className="form-check-input"
                type="checkbox"
                value={permission.id}
                id={permission.id}
                onChange={(e) => onPermissionClick(e, state)}
                checked={state.value[permission.id] ?? false}
              />
              <label className="form-check-label" htmlFor={permission.id}>
                {permission.name}
              </label>
            </>
          </div>
        );
      })}

      {groupParent === "notifications_emails_goals" && renderCustomControls()}
    </div>
  );

  const renderCustomControls = () => (
    <div onClick={(e) => e.stopPropagation()}>
      {customPermissions.map((custom) => {
        return (
          <div className="form-check" key={custom.id}>
            <>
              <input
                {...register(custom.id)}
                id={custom.id}
                className="form-check-input"
                type="checkbox"
              />
              <label className="form-check-label" htmlFor={custom.id}>
                {custom.name}
              </label>
            </>
          </div>
        );
      })}
    </div>
  );

  const renderNestedGroups = (groupParent, hideInnerCaption, state) =>
    Object.keys(allPermissions[groupParent]).map((parent) => {
      let isParent = !Array.isArray(allPermissions[groupParent][parent]);

      if (isParent) {
        return (
          <Fragment key={parent}>
            <div style={{ marginLeft: "20px" }}>{parent}</div>

            {Object.keys(allPermissions[groupParent][parent]).map((group) => {
              return (
                <>
                  <div className="group__item" style={{ marginLeft: "20px" }}>
                    {group}
                  </div>
                  <CCollapse show={group === currentGroupName}>
                    {renderInnerControls({
                      group,
                      groupParent,
                      options: allPermissions[groupParent][parent][group],
                      state
                    })}
                  </CCollapse>
                </>
              );
            })}
          </Fragment>
        );
      }

      return hideInnerCaption ? (
        <Fragment key={parent}>
          {renderInnerControls({
            group: parent,
            groupParent,
            options: allPermissions[groupParent][parent],
            state
          })}
        </Fragment>
      ) : (
        <Fragment key={parent}>
          <div className="group__item" style={{ marginLeft: "20px" }}>
            {parent}
          </div>
          <CCollapse show={parent === currentGroupName}>
            {renderInnerControls({
              group: parent,
              groupParent,
              options: allPermissions[groupParent][parent],
              state
            })}
          </CCollapse>
        </Fragment>
      );
    });

  const renderGroups = (groupParent, hideInnerCaption) => {
    let state =
      groupParent === "location_permissions"
        ? locations
        : groupParent === "reporting_portal_permissions"
        ? corporatePermissions
        : permissions;

    return (
      <div onClick={(e) => onGroupPermissionClick(e)}>
        <CCollapse show={groupParent === permissionTypeKey}>
          {Array.isArray(allPermissions[groupParent])
            ? renderInnerControls({
                group: groupParent,
                groupParent,
                options: allPermissions[groupParent],
                state
              })
            : renderNestedGroups(groupParent, hideInnerCaption, state)}
        </CCollapse>
      </div>
    );
  };

  return (
    <>
      <>
        <div className="light group">
          <div className="group__item" onClick={() => onPermissionTypeClick("charts_permissions")}>
            Chart Permissions
          </div>
          {renderGroups("charts_permissions")}
          {canUpdateLocations && (
            <>
              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("location_permissions")}
              >
                Choose Location Permissions
              </div>
              {renderGroups("location_permissions")}
            </>
          )}
          {canUpdateCorporateReports && (
            <>
              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("reporting_portal_permissions")}
              >
                Reporting Portal Permissions
              </div>
              {renderGroups("reporting_portal_permissions")}
            </>
          )}

          <div className="group__item" onClick={() => onPermissionTypeClick("admin_permissions")}>
            Admin Permissions
          </div>
          {renderGroups("admin_permissions")}

          {canChangeEmails && (
            <>
              <div
                className="group__item"
                onClick={() => onPermissionTypeClick("notifications_emails_goals")}
              >
                Notifications / Emails / Goals
              </div>

              {renderGroups("notifications_emails_goals", true)}
            </>
          )}
        </div>
      </>
    </>
  );
};
